import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useModalStore } from '@/store';
import useTranslation from '@/hooks/useTranslation';
import { BORDER_RADIUS } from '@/constants/config';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

const isWeb = Platform.OS === 'web';

function MemoryEmptyState({ onSelectExample }) {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const styles = useMemo(() => createStyles(colors, palette, isXS), [colors, palette, isXS]);

  const examples = useMemo(
    () => [
      t('emptyState.memory.exampleConcise'),
      t('emptyState.memory.exampleDeveloper'),
      t('emptyState.memory.exampleReact'),
      t('emptyState.memory.exampleDarkMode'),
      t('emptyState.memory.exampleSpanish'),
      t('emptyState.memory.exampleName'),
    ],
    [t]
  );

  const handleExamplePress = useCallback((exampleText) => {
    if (typeof onSelectExample === 'function') {
      onSelectExample(exampleText);
    } else {
      openModal('CreateMemoryModal', { initialContent: exampleText });
    }
  }, [onSelectExample, openModal]);

  return (
    <View style={styles.container}>
      <Text tag="h2" size="l" weight="semibold" style={styles.title}>
        {t('emptyState.memory.title')}
      </Text>
      <Text size="s" color="secondary" style={styles.subtitle}>
        {t('emptyState.memory.subtitle')}
      </Text>
      <View style={styles.examples}>
        {examples.map((example) => (
          <Pressable
            key={example}
            style={({ hovered }) => [
              styles.example,
              hovered && styles.exampleHovered,
            ]}
            onPress={() => handleExamplePress(example)}
            accessibilityRole="button"
            accessibilityLabel={`Add: ${example}`}
          >
            <Text size="s" color="secondary">{example}</Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const createStyles = (colors, palette, isXS) =>
  StyleSheet.create({
    container: {
      width: '100%',
      padding: isXS ? 18 : 24,
      borderWidth: 1,
      borderColor: palette.border,
      backgroundColor: palette.surface,
      borderRadius: 18,
      alignItems: 'flex-start',
      gap: 10,
    },
    title: {
      color: colors.text_color_primary,
    },
    subtitle: {
      maxWidth: 480,
      lineHeight: 22,
    },
    examples: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 8,
    },
    example: {
      paddingVertical: 7,
      paddingHorizontal: 10,
      borderRadius: BORDER_RADIUS.small,
      backgroundColor: palette.preview,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    exampleHovered: {
      backgroundColor: palette.hover,
    },
  });

export default React.memo(MemoryEmptyState);
